import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Save,
  Bell,
  Mail,
  MessageSquare,
  Phone,
  Calendar,
  Clock,
  Users,
  Settings,
  Plus,
  XCircle,
  AlertTriangle
} from 'lucide-react';
import Button from '../../../components/common/Button/Button';

const ReminderNotificationSettings = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [reminder, setReminder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const [notificationSettings, setNotificationSettings] = useState({
    emailNotifications: true,
    smsNotifications: false,
    pushNotifications: true,
    reminderDays: [7, 3, 1],
    customReminderDays: [],
    reminderFrequency: 'once',
    escalationEnabled: true,
    escalationDays: [1, 0], // 1 day before and on due date
    recipients: {
      primary: 'hr_team',
      secondary: [],
      cc: []
    },
    emailTemplate: 'default',
    smsTemplate: 'default',
    timeZone: 'Asia/Kolkata',
    workingHours: {
      start: '09:00',
      end: '18:00',
      days: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday']
    }
  });

  const reminderFrequencies = [
    { value: 'once', label: 'One Time' },
    { value: 'monthly', label: 'Monthly' },
    { value: 'quarterly', label: 'Quarterly' },
    { value: 'annually', label: 'Annually' },
    { value: 'custom', label: 'Custom' }
  ];

  const emailTemplates = [
    { value: 'default', label: 'Default Template' },
    { value: 'urgent', label: 'Urgent Template' },
    { value: 'friendly', label: 'Friendly Template' },
    { value: 'formal', label: 'Formal Template' }
  ];

  const smsTemplates = [
    { value: 'default', label: 'Default SMS' },
    { value: 'short', label: 'Short SMS' },
    { value: 'detailed', label: 'Detailed SMS' }
  ];

  const timeZones = [
    { value: 'Asia/Kolkata', label: 'Asia/Kolkata (IST)' },
    { value: 'UTC', label: 'UTC' },
    { value: 'America/New_York', label: 'America/New_York (EST)' },
    { value: 'Europe/London', label: 'Europe/London (GMT)' }
  ];

  const teams = [
    { value: 'hr_team', label: 'HR Team' },
    { value: 'finance_team', label: 'Finance Team' },
    { value: 'payroll_team', label: 'Payroll Team' },
    { value: 'legal_team', label: 'Legal Team' },
    { value: 'safety_officer', label: 'Safety Officer' },
    { value: 'it_team', label: 'IT Team' },
    { value: 'admin_team', label: 'Admin Team' },
    { value: 'compliance_team', label: 'Compliance Team' }
  ];

  const workingDays = [
    { value: 'monday', label: 'Monday' },
    { value: 'tuesday', label: 'Tuesday' },
    { value: 'wednesday', label: 'Wednesday' },
    { value: 'thursday', label: 'Thursday' },
    { value: 'friday', label: 'Friday' },
    { value: 'saturday', label: 'Saturday' },
    { value: 'sunday', label: 'Sunday' }
  ];

  useEffect(() => {
    loadReminder();
  }, [id]);

  const loadReminder = () => {
    // Mock data - in real app, fetch from API using the ID
    const mockReminder = {
      id: id || '1',
      title: 'PF Returns Filing',
      category: 'Statutory',
      dueDate: '2024-01-15',
      priority: 'high',
      assignedTo: 'HR Team'
    };
    
    setReminder(mockReminder);
    setLoading(false);
  };

  const handleSettingChange = (field, value) => {
    setNotificationSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleNestedSettingChange = (parent, field, value) => {
    setNotificationSettings(prev => ({
      ...prev,
      [parent]: {
        ...prev[parent],
        [field]: value
      }
    }));
  };

  const handleWorkingDayToggle = (day) => {
    const currentDays = notificationSettings.workingHours.days;
    const newDays = currentDays.includes(day)
      ? currentDays.filter(d => d !== day)
      : [...currentDays, day];
    
    handleNestedSettingChange('workingHours', 'days', newDays);
  };

  const addReminderDay = () => {
    const newDay = parseInt(prompt('Enter number of days before due date:'));
    if (newDay && newDay > 0 && !notificationSettings.reminderDays.includes(newDay)) {
      setNotificationSettings(prev => ({
        ...prev,
        reminderDays: [...prev.reminderDays, newDay].sort((a, b) => b - a)
      }));
    }
  };

  const removeReminderDay = (dayToRemove) => {
    setNotificationSettings(prev => ({
      ...prev,
      reminderDays: prev.reminderDays.filter(day => day !== dayToRemove)
    }));
  };

  const addEscalationDay = () => {
    const newDay = parseInt(prompt('Enter number of days before due date for escalation:'));
    if (newDay && newDay >= 0 && !notificationSettings.escalationDays.includes(newDay)) {
      setNotificationSettings(prev => ({
        ...prev,
        escalationDays: [...prev.escalationDays, newDay].sort((a, b) => b - a)
      }));
    }
  };

  const removeEscalationDay = (dayToRemove) => {
    setNotificationSettings(prev => ({
      ...prev,
      escalationDays: prev.escalationDays.filter(day => day !== dayToRemove)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Show success message
      alert('Notification settings updated successfully!');
      
      // Navigate back to reminder detail
      navigate(`/compliance_reminder_detail/${id}`);
      
    } catch (error) {
      alert('Error updating settings. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    navigate(`/compliance_reminder_detail/${id}`);
  };

  const handleTestNotification = () => {
    alert('Test notification sent successfully!');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0F2942] mx-auto mb-4"></div>
          <p className="text-gray-600">Loading notification settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button
            variant="outline"
            icon={<ArrowLeft className="h-4 w-4" />}
            onClick={handleBack}
          >
            Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Notification Settings</h1>
            <p className="text-gray-600">{reminder?.title} - Configure notification preferences</p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <Button
            variant="outline"
            onClick={handleTestNotification}
            icon={<Bell className="h-4 w-4" />}
          >
            Test Notification
          </Button>
          <Button
            variant="outline"
            onClick={handleBack}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            icon={<Save className="h-4 w-4" />}
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Saving...' : 'Save Settings'}
          </Button>
        </div>
      </div>

      {/* Form */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Notification Channels */}
          <div className="border-b border-gray-200 pb-6">
            <div className="flex items-center space-x-3 mb-6">
              <div className="p-2 bg-blue-50 rounded-lg">
                <Bell className="h-5 w-5 text-[#0F2942]" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Notification Channels</h2>
                <p className="text-sm text-gray-600">Choose how you want to receive notifications</p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                <div className="flex items-center space-x-3">
                  <Mail className="h-5 w-5 text-[#0F2942]" />
                  <div>
                    <p className="font-medium text-gray-900">Email Notifications</p>
                    <p className="text-sm text-gray-600">Receive notifications via email</p>
                  </div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={notificationSettings.emailNotifications}
                    onChange={(e) => handleSettingChange('emailNotifications', e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#0F2942]"></div>
                </label>
              </div>

              <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                <div className="flex items-center space-x-3">
                  <MessageSquare className="h-5 w-5 text-green-600" />
                  <div>
                    <p className="font-medium text-gray-900">SMS Notifications</p>
                    <p className="text-sm text-gray-600">Receive notifications via SMS</p>
                  </div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={notificationSettings.smsNotifications}
                    onChange={(e) => handleSettingChange('smsNotifications', e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#0F2942]"></div>
                </label>
              </div>

              <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                <div className="flex items-center space-x-3">
                  <Phone className="h-5 w-5 text-purple-600" />
                  <div>
                    <p className="font-medium text-gray-900">Push Notifications</p>
                    <p className="text-sm text-gray-600">Receive push notifications in the app</p>
                  </div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={notificationSettings.pushNotifications}
                    onChange={(e) => handleSettingChange('pushNotifications', e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#0F2942]"></div>
                </label>
              </div>
            </div>
          </div>

          {/* Reminder Schedule */}
          <div className="border-b border-gray-200 pb-6">
            <div className="flex items-center space-x-3 mb-6">
              <div className="p-2 bg-green-50 rounded-lg">
                <Calendar className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Reminder Schedule</h2>
                <p className="text-sm text-gray-600">Configure when to send reminders</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Reminder Frequency
                </label>
                <select
                  value={notificationSettings.reminderFrequency}
                  onChange={(e) => handleSettingChange('reminderFrequency', e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-[#0F2942]/50"
                >
                  {reminderFrequencies.map(freq => (
                    <option key={freq.value} value={freq.value}>
                      {freq.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Time Zone
                </label>
                <select
                  value={notificationSettings.timeZone}
                  onChange={(e) => handleSettingChange('timeZone', e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-[#0F2942]/50"
                >
                  {timeZones.map(tz => (
                    <option key={tz.value} value={tz.value}>
                      {tz.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="mt-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Reminder Days Before Due Date
              </label>
              <div className="flex flex-wrap gap-2">
                {notificationSettings.reminderDays.map(day => (
                  <span
                    key={day}
                    className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800"
                  >
                    {day} day{day !== 1 ? 's' : ''} before
                    <button
                      type="button"
                      onClick={() => removeReminderDay(day)}
                      className="ml-2 text-[#0F2942] hover:text-blue-800"
                    >
                      <XCircle className="h-3 w-3" />
                    </button>
                  </span>
                ))}
                <button
                  type="button"
                  onClick={addReminderDay}
                  className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-gray-100 text-gray-600 hover:bg-gray-200"
                >
                  <Plus className="h-3 w-3 mr-1" />
                  Add
                </button>
              </div>
            </div>
          </div>

          {/* Escalation Settings */}
          <div className="border-b border-gray-200 pb-6">
            <div className="flex items-center space-x-3 mb-6">
              <div className="p-2 bg-red-50 rounded-lg">
                <AlertTriangle className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Escalation Settings</h2>
                <p className="text-sm text-gray-600">Configure escalation notifications</p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                <div>
                  <p className="font-medium text-gray-900">Enable Escalation</p>
                  <p className="text-sm text-gray-600">Send notifications to managers when overdue</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={notificationSettings.escalationEnabled}
                    onChange={(e) => handleSettingChange('escalationEnabled', e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#0F2942]"></div>
                </label>
              </div>

              {notificationSettings.escalationEnabled && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Escalation Days
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {notificationSettings.escalationDays.map(day => (
                      <span
                        key={day}
                        className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-red-100 text-red-800"
                      >
                        {day === 0 ? 'Due Date' : `${day} day${day !== 1 ? 's' : ''} before`}
                        <button
                          type="button"
                          onClick={() => removeEscalationDay(day)}
                          className="ml-2 text-red-600 hover:text-red-800"
                        >
                          <XCircle className="h-3 w-3" />
                        </button>
                      </span>
                    ))}
                    <button
                      type="button"
                      onClick={addEscalationDay}
                      className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-gray-100 text-gray-600 hover:bg-gray-200"
                    >
                      <Plus className="h-3 w-3 mr-1" />
                      Add
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Recipients */}
          <div className="border-b border-gray-200 pb-6">
            <div className="flex items-center space-x-3 mb-6">
              <div className="p-2 bg-purple-50 rounded-lg">
                <Users className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Recipients</h2>
                <p className="text-sm text-gray-600">Configure who receives notifications</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Primary Recipient
                </label>
                <select
                  value={notificationSettings.recipients.primary}
                  onChange={(e) => handleNestedSettingChange('recipients', 'primary', e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-[#0F2942]/50"
                >
                  {teams.map(team => (
                    <option key={team.value} value={team.value}>
                      {team.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  CC Recipients
                </label>
                <select
                  multiple
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-[#0F2942]/50"
                >
                  {teams.map(team => (
                    <option key={team.value} value={team.value}>
                      {team.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Working Hours */}
          <div className="border-b border-gray-200 pb-6">
            <div className="flex items-center space-x-3 mb-6">
              <div className="p-2 bg-orange-50 rounded-lg">
                <Clock className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Working Hours</h2>
                <p className="text-sm text-gray-600">Configure when to send notifications</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Start Time
                </label>
                <input
                  type="time"
                  value={notificationSettings.workingHours.start}
                  onChange={(e) => handleNestedSettingChange('workingHours', 'start', e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-[#0F2942]/50"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  End Time
                </label>
                <input
                  type="time"
                  value={notificationSettings.workingHours.end}
                  onChange={(e) => handleNestedSettingChange('workingHours', 'end', e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-[#0F2942]/50"
                />
              </div>
            </div>

            <div className="mt-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Working Days
              </label>
              <div className="flex flex-wrap gap-2">
                {workingDays.map(day => (
                  <button
                    key={day.value}
                    type="button"
                    onClick={() => handleWorkingDayToggle(day.value)}
                    className={`px-3 py-1 rounded-full text-sm ${
                      notificationSettings.workingHours.days.includes(day.value)
                        ? 'bg-blue-100 text-blue-800'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {day.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Templates */}
          <div>
            <div className="flex items-center space-x-3 mb-6">
              <div className="p-2 bg-indigo-50 rounded-lg">
                <Settings className="h-5 w-5 text-indigo-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Message Templates</h2>
                <p className="text-sm text-gray-600">Choose notification templates</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Template
                </label>
                <select
                  value={notificationSettings.emailTemplate}
                  onChange={(e) => handleSettingChange('emailTemplate', e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-[#0F2942]/50"
                >
                  {emailTemplates.map(template => (
                    <option key={template.value} value={template.value}>
                      {template.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  SMS Template
                </label>
                <select
                  value={notificationSettings.smsTemplate}
                  onChange={(e) => handleSettingChange('smsTemplate', e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-[#0F2942]/50"
                >
                  {smsTemplates.map(template => (
                    <option key={template.value} value={template.value}>
                      {template.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ReminderNotificationSettings;
